/**
 * `useSigner` — skutočný podpisovateľ komponentu (D1/D5, F4AI-19).
 *
 * `identity` (nsec v `localStorage`) NIE je to isté ako podpisovateľ:
 * ak má prehliadač NIP-07 extension (`window.nostr`), `resolveSigner()`
 * má prednosť a podpisuje INÝ kľúč, než je `identity.pubkey`. Kdekoľvek
 * UI potrebuje pubkey podpisovateľa (claimant do `a` tagu, kontrola
 * autorstva), musí sa pýtať signera, nie identity — inak claim menuje
 * niekoho iného, než event skutočne podpísal.
 */
import { useEffect, useState } from 'react';

import { useIdentity } from './IdentityContext.js';
import { resolveSigner, type Signer } from './signer.js';

export interface SignerState {
  /** null, kým nepreběhne voľba signera (alebo keď nie je žiadny) */
  signer: Signer | null;
  error: string | null;
  loading: boolean;
}

export function useSigner(): SignerState {
  const { identity } = useIdentity();
  const [signer, setSigner] = useState<Signer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const secretKey = identity?.secretKey ?? null;
  const identityPubkey = identity?.pubkey ?? null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    resolveSigner({ secretKey })
      .then((resolved) => {
        if (cancelled) return;
        setSigner(resolved);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSigner(null);
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `identityPubkey` v deps: reset identity vymaže secretKey a nová
    // identita musí prebehnúť znova (NIP-07 cesta je async)
  }, [secretKey, identityPubkey]);

  return { signer, error, loading };
}
